import axios from 'axios';

// Direct API calls to backend server
// Using direct URL instead of proxy
const API_BASE_URL = 'https://api.mosy.in/43.205.212.19:3307/api/';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  },
  // Timeout after 60 seconds (increased for slow productivity queries)
  timeout: 60000,
});

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log('🔄 Making API request to:', config.baseURL + config.url);
    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('✅ API response received:', response.status, response.data);
    return response;
  },
  (error) => {
    console.error('❌ API response error:', error.message);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    return Promise.reject(error);
  }
);

// Company API
export const companyAPI = {
  getCompanies: async () => {
    try {
      console.log('📡 Fetching companies from:', API_BASE_URL + '/company');
      const response = await api.get('/company');
      console.log('Raw API Response:', response.data);

      if (response.data && response.data.isSuccess && response.data.result) {
        console.log('✅ Successfully parsed companies:', response.data.result);
        return response.data.result;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return [];
    } catch (error) {
      console.error('❌ Error fetching companies:', error);

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - CORS issue detected');
        console.error('💡 Possible solutions:');
        console.error('   1. Enable CORS on the backend server');
        console.error('   2. Use a browser extension to disable CORS (for development only)');
        console.error('   3. Contact backend team to add CORS headers');
      }

      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }

      throw error;
    }
  },
};

// Productivity API
export const productivityAPI = {
  getEquipmentwiseProductivityVsTarget: async (filters) => {
    try {
      console.log('📡 Fetching equipment productivity vs target with filters:', filters);
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());

      const response = await api.post('/Productivity/GetEquipmentwiseProductivityVsTarget', filters, {
        timeout: 60000, // 60 second timeout for this specific endpoint
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('Raw Equipment Productivity API Response:', response.data);
      console.log('📊 Response structure:', {
        isSuccess: response.data?.isSuccess,
        hasResult: !!response.data?.result,
        resultType: Array.isArray(response.data?.result) ? 'Array' : typeof response.data?.result,
        resultLength: Array.isArray(response.data?.result) ? response.data?.result.length : 'N/A'
      });

      if (response.data && response.data.isSuccess) {
        const result = response.data.result;
        if (Array.isArray(result)) {
          console.log(`✅ Successfully fetched equipment productivity data: ${result.length} records`);
        } else if (result) {
          console.log('✅ Successfully fetched equipment productivity data:', result);
        } else {
          console.warn('⚠️ API returned success but result is null/undefined');
        }
        return result || null;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return null;
    } catch (error) {
      console.error('❌ Error fetching equipment productivity vs target:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 60 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - CORS issue detected');
      }

      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }

      throw error;
    }
  },

  getOperatorwiseProductivityVsTarget: async (filters) => {
    try {
      console.log('📡 Fetching operator productivity vs target with filters:', filters);
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());

      const response = await api.post('/Productivity/GetOperatorwiseProductivityVsTarget', filters, {
        timeout: 60000,
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('Raw Operator Productivity API Response:', response.data);
      console.log('📊 Response structure:', {
        isSuccess: response.data?.isSuccess,
        hasResult: !!response.data?.result,
        resultType: Array.isArray(response.data?.result) ? 'Array' : typeof response.data?.result,
        resultLength: Array.isArray(response.data?.result) ? response.data?.result.length : 'N/A'
      });

      if (response.data && response.data.isSuccess) {
        const result = response.data.result;
        if (Array.isArray(result)) {
          console.log(`✅ Successfully fetched operator productivity data: ${result.length} records`);
        } else if (result) {
          console.log('✅ Successfully fetched operator productivity data:', result);
        } else {
          console.warn('⚠️ API returned success but result is null/undefined');
        }
        return result || null;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return null;
    } catch (error) {
      console.error('❌ Error fetching operator productivity vs target:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 60 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - CORS issue detected');
      }

      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }

      throw error;
    }
  },

  getEquipmentwiseTotalAvailability: async (filters) => {
    try {
      console.log('📡 Fetching equipment total availability with filters:', filters);
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());

      const response = await api.post('/Productivity/GetEquipmentwiseTotalAvailability', filters, {
        timeout: 60000,
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('Raw Equipment Availability API Response:', response.data);
      console.log('📊 Response structure:', {
        isSuccess: response.data?.isSuccess,
        hasResult: !!response.data?.result,
        resultType: Array.isArray(response.data?.result) ? 'Array' : typeof response.data?.result,
        resultLength: Array.isArray(response.data?.result) ? response.data?.result.length : 'N/A'
      });

      if (response.data && response.data.isSuccess) {
        const result = response.data.result;
        if (Array.isArray(result)) {
          console.log(`✅ Successfully fetched equipment availability data: ${result.length} records`);
        } else if (result) {
          console.log('✅ Successfully fetched equipment availability data:', result);
        } else {
          console.warn('⚠️ API returned success but result is null/undefined');
        }
        return result || null;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return null;
    } catch (error) {
      console.error('❌ Error fetching equipment total availability:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 60 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - CORS issue detected');
      }

      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }

      throw error;
    }
  },

  getOperatorwiseTotalAvailability: async (filters) => {
    try {
      console.log('📡 Fetching operator total availability with filters:', filters);
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());

      const response = await api.post('/Productivity/GetOperatorwiseTotalAvailability', filters, {
        timeout: 60000,
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('Raw Operator Availability API Response:', response.data);
      console.log('📊 Response structure:', {
        isSuccess: response.data?.isSuccess,
        hasResult: !!response.data?.result,
        resultType: Array.isArray(response.data?.result) ? 'Array' : typeof response.data?.result,
        resultLength: Array.isArray(response.data?.result) ? response.data?.result.length : 'N/A'
      });

      if (response.data && response.data.isSuccess) {
        const result = response.data.result;
        if (Array.isArray(result)) {
          console.log(`✅ Successfully fetched operator availability data: ${result.length} records`);
        } else if (result) {
          console.log('✅ Successfully fetched operator availability data:', result);
        } else {
          console.warn('⚠️ API returned success but result is null/undefined');
        }
        return result || null;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return null;
    } catch (error) {
      console.error('❌ Error fetching operator total availability:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 60 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - CORS issue detected');
      }

      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }

      throw error;
    }
  },

  getEquipmentwiseAverageAvailabilityPerDay: async (filters) => {
    try {
      console.log('📡 Fetching equipment average availability per day with filters:', filters);
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());

      const response = await api.post('/Productivity/GetEquipmentwiseAverageAvailabilityPerDay', filters, {
        timeout: 60000,
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('Raw Equipment Avg Availability API Response:', response.data);
      console.log('📊 Response structure:', {
        isSuccess: response.data?.isSuccess,
        hasResult: !!response.data?.result,
        resultType: Array.isArray(response.data?.result) ? 'Array' : typeof response.data?.result,
        resultLength: Array.isArray(response.data?.result) ? response.data?.result.length : 'N/A'
      });

      if (response.data && response.data.isSuccess) {
        const result = response.data.result;
        if (Array.isArray(result)) {
          console.log(`✅ Successfully fetched equipment average availability data: ${result.length} records`);
        } else if (result) {
          console.log('✅ Successfully fetched equipment average availability data:', result);
        } else {
          console.warn('⚠️ API returned success but result is null/undefined');
        }
        return result || null;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return null;
    } catch (error) {
      console.error('❌ Error fetching equipment average availability per day:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 60 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - CORS issue detected');
      }

      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }

      throw error;
    }
  },

  getEquipmentwiseAverageUtilizationPerDay: async (filters) => {
    try {
      console.log('📡 Fetching equipment average utilization per day with filters:', filters);
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());

      const response = await api.post('/Productivity/GetEquipmentwiseAverageUtilizationPerDay', filters, {
        timeout: 60000,
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('Raw Equipment Avg Utilization API Response:', response.data);
      console.log('📊 Response structure:', {
        isSuccess: response.data?.isSuccess,
        hasResult: !!response.data?.result,
        resultType: Array.isArray(response.data?.result) ? 'Array' : typeof response.data?.result,
        resultLength: Array.isArray(response.data?.result) ? response.data?.result.length : 'N/A'
      });

      if (response.data && response.data.isSuccess) {
        const result = response.data.result;
        if (Array.isArray(result)) {
          console.log(`✅ Successfully fetched equipment average utilization data: ${result.length} records`);
        } else if (result) {
          console.log('✅ Successfully fetched equipment average utilization data:', result);
        } else {
          console.warn('⚠️ API returned success but result is null/undefined');
        }
        return result || null;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return null;
    } catch (error) {
      console.error('❌ Error fetching equipment average utilization per day:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 60 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - CORS issue detected');
      }

      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }

      throw error;
    }
  },

  getOperatorwiseAverageUtilizationPerDay: async (filters) => {
    try {
      console.log('📡 Fetching operator average utilization per day with filters:', filters);
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());

      const response = await api.post('/Productivity/GetOperatorwiseAverageUtilizationPerDay', filters, {
        timeout: 60000,
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('Raw Operator Avg Utilization API Response:', response.data);
      console.log('📊 Response structure:', {
        isSuccess: response.data?.isSuccess,
        hasResult: !!response.data?.result,
        resultType: Array.isArray(response.data?.result) ? 'Array' : typeof response.data?.result,
        resultLength: Array.isArray(response.data?.result) ? response.data?.result.length : 'N/A'
      });

      if (response.data && response.data.isSuccess) {
        const result = response.data.result;
        if (Array.isArray(result)) {
          console.log(`✅ Successfully fetched operator average utilization data: ${result.length} records`);
        } else if (result) {
          console.log('✅ Successfully fetched operator average utilization data:', result);
        } else {
          console.warn('⚠️ API returned success but result is null/undefined');
        }
        return result || null;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return null;
    } catch (error) {
      console.error('❌ Error fetching operator average utilization per day:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 60 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - CORS issue detected');
      }

      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }

      throw error;
    }
  },
};

// Productivity Analytics API - Single endpoint for all productivity dashboard data
const ANALYTICS_API_URL = 'https://api.mosy.in/43.205.212.19:3307/api/';

export const productivityAnalyticsAPI = {
  getAnalytics: async (filters = {}) => {
    try {
      console.log('📡 Fetching productivity analytics from:', ANALYTICS_API_URL + '/productivity/analytics');
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());
      console.log('📋 Analytics filters received:', filters);

      // Get JWT token and legacyToken from localStorage or sessionStorage
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      const legacyToken = localStorage.getItem('legacyToken') || sessionStorage.getItem('legacyToken');
      console.log('🔑 Auth token present:', token ? 'Yes (' + token.substring(0, 20) + '...)' : 'No');
      console.log('🔑 Legacy token present:', legacyToken ? 'Yes (' + legacyToken.substring(0, 20) + '...)' : 'No');

      if (!legacyToken) {
        console.warn('⚠️ No legacyToken found in storage');
      }

      // Resolve companyId: prefer explicit filter, then selectedCompany, then stored value
      const storedCompanyId = localStorage.getItem('companyId') || sessionStorage.getItem('companyId');
      const resolvedCompanyId = filters.companyId || filters.selectedCompany || storedCompanyId;

      if (!resolvedCompanyId) {
        console.warn('⚠️ No companyId provided or stored for productivity analytics request');
      } else {
        console.log('🏢 Using companyId for analytics:', resolvedCompanyId);
      }

      // Build query params for POST request in specific order:
      // 1. companyId
      // 2. endDate
      // 3. startDate
      // 4. Other params (locationId, equipmentId, shiftNo, etc.)
      const { selectedCompany, equipment_id, shift_no, location_id, companyId: explicitCompanyId, startDate, endDate, ...restFilters } = filters;

      // Build queryParams object in the exact order required
      const queryParams = {};

      // 1. companyId first
      if (resolvedCompanyId) {
        queryParams.companyId = resolvedCompanyId;
      }

      // 2. endDate second
      const finalEndDate = endDate || restFilters.endDate;
      if (finalEndDate) {
        queryParams.endDate = finalEndDate;
      }

      // 3. startDate third
      const finalStartDate = startDate || restFilters.startDate;
      if (finalStartDate) {
        queryParams.startDate = finalStartDate;
      }

      // 4. locationId fourth
      const finalLocationId = location_id || restFilters.locationId;
      if (finalLocationId) {
        queryParams.location_id = finalLocationId;
      }

      // 5. Other params (equipment_id, shiftNo, selectedEquipment, selectedOperator, etc.)
      const finalEquipmentId = equipment_id || restFilters.equipmentId;
      if (finalEquipmentId) {
        queryParams.equipment_id = finalEquipmentId;
      }

      const finalShiftNo = shift_no || restFilters.shift_no;
      if (finalShiftNo) {
        queryParams.shift_no = finalShiftNo;
      }

      // Add any other remaining filters (selectedEquipment, selectedOperator, etc.)
      Object.keys(restFilters).forEach(key => {
        if (!['locationId', 'equipmentId', 'shift_no', 'startDate', 'endDate'].includes(key)) {
          queryParams[key] = restFilters[key];
        }
      });

      console.log('📤 Final analytics query params:', queryParams);

      // Prepare request body with legacyToken
      const requestBody = {
        authorization: legacyToken || '',
      };

      console.log('📦 Request body:', { authorization: legacyToken ? legacyToken.substring(0, 20) + '...' : 'empty' });

      const analyticsApi = axios.create({
        baseURL: ANALYTICS_API_URL,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        timeout: 60000,
      });

      const response = await analyticsApi.post('/productivity/analytics', requestBody, {
        params: queryParams,
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('✅ Productivity analytics response:', response.data);

      if (response.data && response.data.success) {
        console.log('✅ Successfully fetched productivity analytics data');
        return response.data.data;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return null;
    } catch (error) {
      console.error('❌ Error fetching productivity analytics:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 60 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - Unable to reach analytics server');
      }

      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }

      throw error;
    }
  },
};

export const fuelAnalyticsAPI = {
  getAnalytics: async (filters = {}) => {
    try {
      console.log('📡 Fetching fuel analytics from:', ANALYTICS_API_URL + '/fuel-analytics');
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());
      console.log('📋 Fuel analytics filters received:', filters);

      // Get token from localStorage or sessionStorage
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      console.log('🔑 Auth token present:', token ? 'Yes (' + token.substring(0, 20) + '...)' : 'No');

      // Resolve companyId: prefer explicit filter, then selectedCompany, then stored value
      const storedCompanyId = localStorage.getItem('companyId') || sessionStorage.getItem('companyId');
      const resolvedCompanyId = filters.companyId || filters.selectedCompany || storedCompanyId;

      if (!resolvedCompanyId) {
        console.warn('⚠️ No companyId provided or stored for fuel analytics request');
      } else {
        console.log('🏢 Using companyId for fuel analytics:', resolvedCompanyId);
      }

      // Build query params for GET request
      // Map to API parameter names: equipmentId (not equipment_id), shiftNo (not shift_no)
      // Do not forward helper-only props like selectedCompany to the backend.
      const { selectedCompany, equipment_id, shift_no, location_id, companyId: explicitCompanyId, ...restFilters } = filters;
      const queryParams = {
        companyId: resolvedCompanyId,
        ...restFilters,
      };

      // Map snake_case to camelCase for API
      if (equipment_id) {
        queryParams.equipmentId = equipment_id;
      }
      if (shift_no) {
        queryParams.shiftNo = shift_no;
      }
      if (location_id) {
        queryParams.locationId = location_id;
      }
      if (filters.operatorId) {
        queryParams.operatorId = filters.operatorId;
      }

      console.log('📤 Final fuel analytics query params:', queryParams);

      const analyticsApi = axios.create({
        baseURL: ANALYTICS_API_URL,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        timeout: 60000,
      });

      const response = await analyticsApi.get('/fuel-analytics', {
        params: queryParams,
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('✅ Fuel analytics response:', response.data);

      if (response.data && response.data.success) {
        console.log('✅ Successfully fetched fuel analytics data');
        return response.data;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return null;
    } catch (error) {
      console.error('❌ Error fetching fuel analytics:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 60 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - Unable to reach analytics server');
      }

      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }

      throw error;
    }
  },
};

// Maintenance Analytics API
export const maintenanceAnalyticsAPI = {
  getAnalytics: async (filters = {}) => {
    try {
      console.log('📡 Fetching maintenance analytics from:', ANALYTICS_API_URL + '/maintenance/analytics');
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());
      console.log('📋 Maintenance analytics filters received:', filters);

      // Get token from localStorage or sessionStorage
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      console.log('🔑 Auth token present:', token ? 'Yes (' + token.substring(0, 20) + '...)' : 'No');

      // Resolve companyId: prefer explicit filter, then selectedCompany, then stored value
      const storedCompanyId = localStorage.getItem('companyId') || sessionStorage.getItem('companyId');
      const resolvedCompanyId = filters.companyId || filters.selectedCompany || storedCompanyId;

      if (!resolvedCompanyId) {
        console.warn('⚠️ No companyId provided or stored for maintenance analytics request');
      } else {
        console.log('🏢 Using companyId for maintenance analytics:', resolvedCompanyId);
      }

      // Build query params for GET request
      const { selectedCompany, equipment_id, shift_no, location_id, companyId: explicitCompanyId, ...restFilters } = filters;
      const queryParams = {
        companyId: resolvedCompanyId,
        ...restFilters,
      };

      // Map snake_case to camelCase for API
      if (equipment_id) {
        queryParams.equipmentId = equipment_id;
      }
      if (shift_no) {
        queryParams.shiftNo = shift_no;
      }
      if (location_id) {
        queryParams.locationId = location_id;
      }

      console.log('📤 Final maintenance analytics query params:', queryParams);

      const analyticsApi = axios.create({
        baseURL: ANALYTICS_API_URL,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        timeout: 60000,
      });

      const response = await analyticsApi.get('/maintenance/analytics', {
        params: queryParams,
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('✅ Maintenance analytics response:', response.data);

      if (response.data && response.data.success) {
        console.log('✅ Successfully fetched maintenance analytics data');
        return response.data;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return null;
    } catch (error) {
      console.error('❌ Error fetching maintenance analytics:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 60 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - Unable to reach analytics server');
      }

      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }

      throw error;
    }
  },
};

export const predictiveMaintenanceAPI = {
  getPredictive: async (filters = {}) => {
    try {
      console.log('📡 Fetching predictive maintenance from:', ANALYTICS_API_URL + '/maintenance/predictive');
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());
      console.log('📋 Predictive maintenance filters received:', filters);

      // Get token from localStorage or sessionStorage
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      console.log('🔑 Auth token present:', token ? 'Yes (' + token.substring(0, 20) + '...)' : 'No');

      // Resolve companyId: prefer explicit filter, then selectedCompany, then stored value
      const storedCompanyId = localStorage.getItem('companyId') || sessionStorage.getItem('companyId');
      const resolvedCompanyId = filters.companyId || filters.selectedCompany || storedCompanyId;

      if (!resolvedCompanyId) {
        console.warn('⚠️ No companyId provided or stored for predictive maintenance request');
      } else {
        console.log('🏢 Using companyId for predictive maintenance:', resolvedCompanyId);
      }

      // Build query params for GET request
      const { selectedCompany, equipment_id, location_id, companyId: explicitCompanyId, ...restFilters } = filters;
      const queryParams = {
        companyId: resolvedCompanyId,
        ...restFilters,
      };

      // Map snake_case to camelCase for API
      if (equipment_id) {
        queryParams.equipmentId = equipment_id;
      }
      if (location_id) {
        queryParams.locationId = location_id;
      }

      console.log('📤 Final predictive maintenance query params:', queryParams);

      const analyticsApi = axios.create({
        baseURL: ANALYTICS_API_URL,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        timeout: 60000,
      });

      const response = await analyticsApi.get('/maintenance/predictive', {
        params: queryParams,
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('✅ Predictive maintenance response:', response.data);

      if (response.data && response.data.success) {
        console.log('✅ Successfully fetched predictive maintenance data');
        return response.data;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return null;
    } catch (error) {
      console.error('❌ Error fetching predictive maintenance:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 60 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - Unable to reach analytics server');
      }

      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }

      throw error;
    }
  },
};

// Spares Analytics API
export const sparesAnalyticsAPI = {
  getAnalytics: async (filters = {}) => {
    try {
      console.log('📡 Fetching spares analytics from:', ANALYTICS_API_URL + '/spares/analytics');
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());
      console.log('📋 Spares analytics filters received:', filters);

      // Get token from storage
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

      // Resolve companyId - prefer explicit companyId, fallback to selectedCompany, then stored companyId
      const resolvedCompanyId = filters.companyId || filters.selectedCompany ||
        localStorage.getItem('companyId') || sessionStorage.getItem('companyId');

      if (!resolvedCompanyId) {
        console.warn('⚠️ No companyId available for spares analytics request');
        throw new Error('Company ID is required');
      }

      // Map to API parameter names
      const { selectedCompany, equipment_id, shift_no, location_id, companyId: explicitCompanyId, ...restFilters } = filters;
      const queryParams = {
        companyId: resolvedCompanyId,
        ...restFilters,
      };

      // Map equipment_id to equipmentId if present
      if (equipment_id) {
        queryParams.equipmentId = equipment_id;
      }
      // Map location_id to locationId if present
      if (location_id) {
        queryParams.locationId = location_id;
      }
      // Map shift_no to shiftNo if present (though spares might not use shift)
      if (shift_no) {
        queryParams.shiftNo = shift_no;
      }

      // Map fromDate/toDate if present (API uses fromDate/toDate)
      if (filters.fromDate) {
        queryParams.fromDate = filters.fromDate;
      }
      if (filters.toDate) {
        queryParams.toDate = filters.toDate;
      }
      // Also support startDate/endDate for consistency
      if (filters.startDate && !filters.fromDate) {
        queryParams.fromDate = filters.startDate;
      }
      if (filters.endDate && !filters.toDate) {
        queryParams.toDate = filters.endDate;
      }

      console.log('📤 Final spares analytics query params:', queryParams);

      const analyticsApi = axios.create({
        baseURL: ANALYTICS_API_URL,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        timeout: 60000,
      });

      const response = await analyticsApi.get('/spares/analytics', {
        params: queryParams,
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('✅ Spares analytics response:', response.data);

      if (response.data && response.data.success) {
        console.log('✅ Successfully fetched spares analytics data');
        return response.data;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return null;
    } catch (error) {
      console.error('❌ Error fetching spares analytics:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 60 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - Unable to reach analytics server');
      }

      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
      }

      throw error;
    }
  },
};

// Equipment API
export const equipmentAPI = {
  getEquipment: async (companyId) => {
    try {
      console.log('📡 Fetching equipment list for company:', companyId);
      console.log('⏱️ Request started at:', new Date().toLocaleTimeString());

      // Get token from localStorage or sessionStorage
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
      console.log('🔑 Auth token present:', token ? 'Yes (' + token.substring(0, 20) + '...)' : 'No');

      // Create authenticated API instance
      const equipmentApi = axios.create({
        baseURL: API_BASE_URL,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        timeout: 30000,
      });

      const response = await equipmentApi.get('/equipment', {
        params: { companyId },
      });

      console.log('⏱️ Request completed at:', new Date().toLocaleTimeString());
      console.log('Raw Equipment API Response:', response.data);

      // Handle different response structures
      let equipmentList = null;

      if (response.data && response.data.isSuccess && response.data.result) {
        // Response wrapped in { isSuccess: true, result: [...] }
        equipmentList = response.data.result;
      } else if (Array.isArray(response.data)) {
        // Response is directly an array
        equipmentList = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        // Response wrapped in { data: [...] }
        equipmentList = response.data.data;
      }

      if (equipmentList && Array.isArray(equipmentList)) {
        // Map to expected format: { machineId, machineName }
        // Use id for machineId and equipment_registration_no for machineName
        const mappedEquipment = equipmentList.map((eq) => ({
          machineId: eq.id,
          machineName: eq.equipment_registration_no || eq.equipment_name || eq.equipmentName || eq.name || 'Unknown',
        }));

        console.log(`✅ Successfully fetched equipment list: ${mappedEquipment.length} machines`);
        return mappedEquipment;
      }

      console.warn('⚠️ Unexpected API response structure:', response.data);
      return [];
    } catch (error) {
      console.error('❌ Error fetching equipment list:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Request timed out after 30 seconds');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - CORS issue detected');
      }

      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);

        if (error.response.status === 401) {
          console.error('🔒 Unauthorized - Token may be missing or invalid');
        }
      }

      throw error;
    }
  },
};

// Auth API - Login endpoint
const AUTH_API_URL = 'https://api.mosy.in/43.205.212.19:3307/api/';

export const authAPI = {
  login: async (username, password) => {
    try {
      console.log('🔐 Attempting login for user:', username);

      const loginApi = axios.create({
        baseURL: AUTH_API_URL,
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        timeout: 30000,
      });

      const response = await loginApi.post('/auth/login', {
        username: username,
        password: password,
      });

      console.log('✅ Login API Response:', response.data);

      if (response.data && response.data.success) {
        console.log('✅ Login successful');
        return response.data;
      } else {
        console.warn('⚠️ Login failed:', response.data?.message || 'Unknown error');
        throw new Error(response.data?.message || 'Login failed');
      }
    } catch (error) {
      console.error('❌ Error during login:', error);

      if (error.code === 'ECONNABORTED') {
        console.error('⏱️ Login request timed out');
        throw new Error('Login request timed out. Please try again.');
      }

      if (error.code === 'ERR_NETWORK') {
        console.error('🌐 Network Error - Unable to reach login server');
        throw new Error('Network error. Please check your connection.');
      }

      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);

        const errorMessage = error.response.data?.message ||
          `Login failed (${error.response.status})`;
        throw new Error(errorMessage);
      }

      throw error;
    }
  },

  logout: async () => {
    console.log('🚪 Logging out...');

    // Get the stored token
    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

    if (token) {
      try {
        const logoutApi = axios.create({
          baseURL: AUTH_API_URL,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          timeout: 30000,
        });

        await logoutApi.post('/auth/logout');
        console.log('✅ Logout API call successful');
      } catch (error) {
        console.error('⚠️ Logout API error:', error.message);
        // Continue with local logout even if API call fails
      }
    }

    // Clear all authentication data from localStorage
    localStorage.removeItem('authToken');
    localStorage.removeItem('username');
    localStorage.removeItem('userData');
    localStorage.removeItem('companyId');

    // Clear all authentication data from sessionStorage
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('username');
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('companyId');

    console.log('✅ Logout successful - cleared all authentication data');
  },
};

export default api;
